import React, { useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";

const inputClass = "bg-[#F6F1E8] border border-[#1A1714]/15 rounded-sm focus-visible:ring-1 focus-visible:ring-[#1A1714]";

const Card = ({ children, onRemove, onUp, onDown }) => (
  <div className="bg-[#EFE8DA] border border-[#1A1714]/15 rounded-sm p-5 relative">
    <div className="absolute top-3 right-3 flex items-center gap-1">
      {onUp && (<button type="button" onClick={onUp} className="p-1.5 rounded hover:bg-[#1A1714]/10" title="Move up"><ChevronUp size={14} /></button>)}
      {onDown && (<button type="button" onClick={onDown} className="p-1.5 rounded hover:bg-[#1A1714]/10" title="Move down"><ChevronDown size={14} /></button>)}
      {onRemove && (<button type="button" onClick={onRemove} className="p-1.5 rounded hover:bg-[#B5523A]/15 text-[#B5523A]" title="Remove"><Trash2 size={14} /></button>)}
    </div>
    {children}
  </div>
);

const AddBtn = ({ onClick, label }) => (
  <button type="button" onClick={onClick} className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase border border-dashed border-[#1A1714]/30 px-4 py-2 rounded-sm hover:border-[#1A1714] hover:bg-[#1A1714] hover:text-[#F6F1E8] transition-colors">
    <Plus size={14} /> {label}
  </button>
);

const arrSwap = (arr, i, j) => {
  if (j < 0 || j >= arr.length) return arr;
  const copy = [...arr];
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy;
};

export function StatsEditor({ stats, onChange }) {
  return (
    <div className="max-w-3xl space-y-4">
      <p className="text-[#1A1714]/65 text-sm">Up to 4 stats display on the hero.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {stats.map((s, i) => (
          <Card key={i} onRemove={() => onChange(stats.filter((_, k) => k !== i))}>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Value</label>
                <Input className={inputClass} value={s.value} onChange={(e) => onChange(stats.map((x, k) => (k === i ? { ...x, value: e.target.value } : x)))} />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Label</label>
                <Input className={inputClass} value={s.label} onChange={(e) => onChange(stats.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)))} />
              </div>
            </div>
          </Card>
        ))}
      </div>
      {stats.length < 4 && <AddBtn onClick={() => onChange([...stats, { value: "", label: "" }])} label="Add stat" />}
    </div>
  );
}

export function SkillsEditor({ skills, onChange }) {
  return (
    <div className="space-y-4 max-w-4xl">
      <p className="text-[#1A1714]/65 text-sm">Group your skills into categories. One item per line.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {skills.map((s, i) => (
          <Card key={i}
            onRemove={() => onChange(skills.filter((_, k) => k !== i))}
            onUp={i > 0 ? () => onChange(arrSwap(skills, i, i - 1)) : null}
            onDown={i < skills.length - 1 ? () => onChange(arrSwap(skills, i, i + 1)) : null}
          >
            <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Group title</label>
            <Input className={inputClass} value={s.group} onChange={(e) => onChange(skills.map((x, k) => (k === i ? { ...x, group: e.target.value } : x)))} />
            <label className="mt-3 block text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Items (one per line)</label>
            <Textarea rows={5} className={inputClass} value={(s.items || []).join("\n")} onChange={(e) => onChange(skills.map((x, k) => (k === i ? { ...x, items: e.target.value.split("\n").map((v) => v).filter((v) => v.trim() !== "") } : x)))} />
          </Card>
        ))}
      </div>
      <AddBtn onClick={() => onChange([...skills, { group: "New group", items: [] }])} label="Add skill group" />
    </div>
  );
}

export function ExperienceEditor({ experience, onChange }) {
  return (
    <div className="space-y-4 max-w-4xl">
      {experience.map((x, i) => (
        <Card key={i}
          onRemove={() => onChange(experience.filter((_, k) => k !== i))}
          onUp={i > 0 ? () => onChange(arrSwap(experience, i, i - 1)) : null}
          onDown={i < experience.length - 1 ? () => onChange(arrSwap(experience, i, i + 1)) : null}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Role</label>
              <Input className={inputClass} value={x.role} onChange={(e) => onChange(experience.map((y, k) => (k === i ? { ...y, role: e.target.value } : y)))} />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Company</label>
              <Input className={inputClass} value={x.company} onChange={(e) => onChange(experience.map((y, k) => (k === i ? { ...y, company: e.target.value } : y)))} />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Period</label>
              <Input className={inputClass} value={x.period} onChange={(e) => onChange(experience.map((y, k) => (k === i ? { ...y, period: e.target.value } : y)))} />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Location</label>
              <Input className={inputClass} value={x.location} onChange={(e) => onChange(experience.map((y, k) => (k === i ? { ...y, location: e.target.value } : y)))} />
            </div>
          </div>
          <label className="mt-3 block text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Bullets (one per line)</label>
          <Textarea rows={5} className={inputClass} value={(x.bullets || []).join("\n")} onChange={(e) => onChange(experience.map((y, k) => (k === i ? { ...y, bullets: e.target.value.split("\n").filter((v) => v.trim() !== "") } : y)))} />
        </Card>
      ))}
      <AddBtn onClick={() => onChange([...experience, { role: "", company: "", period: "", location: "", bullets: [] }])} label="Add experience" />
    </div>
  );
}

export function EducationEditor({ education, onChange }) {
  return (
    <div className="space-y-4 max-w-4xl">
      {education.map((e, i) => (
        <Card key={i}
          onRemove={() => onChange(education.filter((_, k) => k !== i))}
          onUp={i > 0 ? () => onChange(arrSwap(education, i, i - 1)) : null}
          onDown={i < education.length - 1 ? () => onChange(arrSwap(education, i, i + 1)) : null}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">School / institution</label>
              <Input className={inputClass} value={e.school} onChange={(ev) => onChange(education.map((y, k) => (k === i ? { ...y, school: ev.target.value } : y)))} />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Period</label>
              <Input className={inputClass} value={e.period} onChange={(ev) => onChange(education.map((y, k) => (k === i ? { ...y, period: ev.target.value } : y)))} />
            </div>
          </div>
          <label className="mt-3 block text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Degree / programme</label>
          <Input className={inputClass} value={e.degree} onChange={(ev) => onChange(education.map((y, k) => (k === i ? { ...y, degree: ev.target.value } : y)))} />
          <label className="mt-3 block text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Detail</label>
          <Textarea rows={3} className={inputClass} value={e.detail} onChange={(ev) => onChange(education.map((y, k) => (k === i ? { ...y, detail: ev.target.value } : y)))} />
        </Card>
      ))}
      <AddBtn onClick={() => onChange([...education, { school: "", degree: "", period: "", detail: "" }])} label="Add education / certificate" />
    </div>
  );
}
